// Payroll overview — hourly rates + day-of-week multipliers + per-employee totals.
// Manager + Owner only. Net hours = shift hours - pause. Amount = net × rate × multiplier.
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, Chip, EmptyState } from "@/components/ui-bits";
import LocationScopeSelect from "@/components/LocationScopeSelect";
import {
  ChevronLeft, ChevronRight, Printer, Download, Settings2, Wallet, Coins, Pencil,
  ChevronDown, ChevronRight as ChevronRightIcon, X, Calculator, MapPin, Globe2,
} from "lucide-react";
import { toast } from "sonner";
import AnimatedNumber from "@/components/AnimatedNumber";

const DOW = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// ---------- Date helpers ----------
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const startOfWeek = (d) => {
  const x = new Date(d); const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x;
};
const startOfMonth = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), 1); return x; };

// ---------- Currency ----------
const currencyFmt = (amount, cur = "EUR", lang = "nl") => {
  try {
    return new Intl.NumberFormat(lang === "en" ? "en-GB" : "nl-NL", {
      style: "currency", currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(amount || 0);
  } catch {
    return `€ ${(amount || 0).toFixed(2)}`;
  }
};

// ============================================================
// Main page
// ============================================================
export default function Payroll() {
  const { t, lang } = useI18n();
  const [period, setPeriod] = useState("week"); // "week" | "month"
  const [anchor, setAnchor] = useState(() => fmt(new Date()));
  // Feb 2026: local location filter — independent of the global scope so
  // the boekhouder can export one vestiging without changing app-wide view.
  const [locationFilter, setLocationFilter] = useState("");
  const [locations, setLocations] = useState([]);
  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [rates, setRates] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [expanded, setExpanded] = useState({});
  const printRef = useRef(null);

  const load = async () => {
    const params = { period, start: anchor, __no_scope: true };
    if (locationFilter) params.location_id = locationFilter;
    const [ov, st, rt, locs] = await Promise.all([
      api.get(`/payroll/overview`, { params }),
      api.get(`/payroll/settings`),
      api.get(`/payroll/rates`),
      api.get(`/locations`, { params: { __no_scope: true } }).catch(() => ({ data: [] })),
    ]);
    setOverview(ov.data); setSettings(st.data); setRates(rt.data);
    setLocations(locs.data || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period, anchor, locationFilter]);

  const shiftAnchor = (delta) => {
    const d = new Date(anchor + "T00:00:00");
    if (period === "week") { d.setDate(d.getDate() + delta * 7); }
    else { d.setMonth(d.getMonth() + delta); }
    setAnchor(fmt(d));
  };
  const jumpToToday = () => setAnchor(fmt(new Date()));

  const rateByUser = useMemo(() => {
    const m = {}; for (const r of rates) m[r._id] = r; return m;
  }, [rates]);

  const periodLabel = useMemo(() => {
    if (!overview) return "";
    const s = new Date(overview.start + "T00:00:00");
    const e = new Date(overview.end + "T00:00:00");
    const locale = lang === "en" ? "en-GB" : "nl-NL";
    if (period === "month") {
      return s.toLocaleDateString(locale, { month: "long", year: "numeric" });
    }
    return `${s.toLocaleDateString(locale, { day: "numeric", month: "short" })} – ${e.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}`;
  }, [overview, period, lang]);

  const currency = overview?.currency || "EUR";

  const handlePrint = () => {
    document.body.classList.add("payroll-print-mode");
    // Slight delay so the print class applies before print dialog
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("payroll-print-mode"), 300);
    }, 60);
  };

  // Export the current overview as a CSV file for the accountant. Includes
  // a summary section (one row per employee) followed by a detailed shift
  // breakdown. Semicolon-separated + BOM so Excel opens it correctly in NL.
  const handleExportCsv = () => {
    if (!overview) return;
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      // Wrap in quotes and escape internal quotes when needed
      if (/[";\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const fmtNum = (n, d = 2) => (Number(n) || 0).toFixed(d).replace(".", ",");
    const cur = overview.currency || "EUR";

    const lines = [];
    const locName = locationFilter ? (locations.find((l) => l._id === locationFilter)?.name || "") : "";
    // Metadata header
    lines.push(esc(`Rostr · ${t("payroll_page_title")}`));
    lines.push(esc(`${t("payroll_period_" + period)}: ${periodLabel}`));
    if (locName) lines.push(esc(`${t("location")}: ${locName}`));
    lines.push(esc(`${t("payroll_export_generated_at") || "Gegenereerd op"}: ${new Date().toLocaleString(lang === "en" ? "en-GB" : "nl-NL")}`));
    lines.push(esc(`${t("payroll_grand_total")}: ${fmtNum(overview.grand_total_amount)} ${cur}`));
    lines.push(esc(`${t("payroll_total_hours")}: ${fmtNum(overview.grand_total_hours, 1)}`));
    lines.push("");
    // Summary rows
    lines.push([
      t("employee"),
      t("role"),
      t("payroll_hourly_rate"),
      t("payroll_col_shifts") || "Diensten",
      t("payroll_total_hours"),
      t("payroll_amount"),
    ].map(esc).join(";"));
    for (const r of overview.rows) {
      lines.push([
        r.name,
        t(`role_${r.role}`) || r.role,
        fmtNum(r.hourly_rate),
        r.shifts.length,
        fmtNum(r.total_hours),
        fmtNum(r.total_amount),
      ].map(esc).join(";"));
    }
    // Detailed breakdown
    lines.push("");
    lines.push(esc(t("payroll_export_details_header") || "Detail per dienst"));
    lines.push([
      t("employee"),
      t("date"),
      t("day"),
      t("time") || "Tijd",
      t("payroll_gross_hours"),
      t("payroll_pause"),
      t("payroll_net"),
      t("payroll_multiplier"),
      t("payroll_amount"),
    ].map(esc).join(";"));
    for (const r of overview.rows) {
      for (const s of r.shifts) {
        lines.push([
          r.name,
          s.date,
          t(`dow_${s.dow}`) || s.dow,
          `${s.start_time}-${s.end_time}`,
          fmtNum(s.gross_hours),
          s.pause_minutes > 0 ? `-${fmtNum(s.pause_minutes / 60)}` : "",
          fmtNum(s.net_hours),
          `${Math.round(s.multiplier_pct)}%`,
          fmtNum(s.amount),
        ].map(esc).join(";"));
      }
    }

    const csv = "\uFEFF" + lines.join("\n"); // BOM for Excel-NL
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safePeriod = (overview.start || anchor).replaceAll("-", "");
    const locSlug = locationFilter
      ? "-" + (locations.find((l) => l._id === locationFilter)?.name || "loc")
          .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
      : "";
    a.href = url;
    a.download = `rostr-loonoverzicht-${period}-${safePeriod}${locSlug}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t("payroll_export_success") || "CSV gedownload");
  };

  return (
    <div data-testid="page-payroll">
      <PageHeader
        eyebrow={t("payroll_label")}
        title={t("payroll_page_title")}
        subtitle={t("payroll_page_subtitle")}
      />

      {/* Toolbar — losstaand van de header in een eigen kaart. Bovenaan: periode-
          keuze, navigatie & huidige periode-label. Onderin: actie-knoppen zodat
          álles altijd zichtbaar is zonder scrollen. Feb 2026 refactor. */}
      <div className="card p-3 md:p-4 mb-5 flex flex-col gap-3 no-print" data-testid="payroll-toolbar">
        <div className="flex flex-wrap items-center gap-2.5" data-testid="payroll-controls">
          {/* Period tabs */}
          <div className="inline-flex rounded-xl border hairline overflow-hidden bg-[hsl(var(--bg))]">
            <button
              onClick={() => setPeriod("week")}
              className={`px-4 h-10 text-[13px] font-semibold transition-colors ${period === "week" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--muted))]"}`}
              data-testid="payroll-tab-week"
            >
              {t("payroll_period_week")}
            </button>
            <button
              onClick={() => setPeriod("month")}
              className={`px-4 h-10 text-[13px] font-semibold transition-colors ${period === "month" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--muted))]"}`}
              data-testid="payroll-tab-month"
            >
              {t("payroll_period_month")}
            </button>
          </div>

          {/* Navigation cluster */}
          <div className="flex items-center h-10 border hairline rounded-xl bg-[hsl(var(--bg))] overflow-hidden">
            <button
              onClick={() => shiftAnchor(-1)}
              className="w-10 h-10 grid place-items-center text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--muted))] transition-colors"
              data-testid="payroll-prev"
              aria-label={t("payroll_prev_period")}
            >
              <ChevronLeft size={15} strokeWidth={2} />
            </button>
            <div className="w-px h-5 bg-[hsl(var(--border))]" />
            <button
              onClick={jumpToToday}
              className="px-3.5 h-10 text-[12.5px] font-semibold text-[hsl(var(--text-strong))] hover:bg-[hsl(var(--muted))] transition-colors whitespace-nowrap"
              data-testid="payroll-today"
            >
              {period === "week" ? t("payroll_this_week") : t("payroll_this_month")}
            </button>
            <div className="w-px h-5 bg-[hsl(var(--border))]" />
            <button
              onClick={() => shiftAnchor(1)}
              className="w-10 h-10 grid place-items-center text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--muted))] transition-colors"
              data-testid="payroll-next"
              aria-label={t("payroll_next_period")}
            >
              <ChevronRight size={15} strokeWidth={2} />
            </button>
          </div>

          {/* Current period label — prominent, always visible */}
          <div
            className="h-10 px-3.5 inline-flex items-center rounded-xl border hairline bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] text-[13px] font-bold tabular-nums whitespace-nowrap"
            data-testid="payroll-period-label"
          >
            {periodLabel || "—"}
          </div>

          {/* Location filter — Fase 2: unified LocationScopeSelect. Hidden
              automatically for 0/1 locations (see component). */}
          <LocationScopeSelect
            locations={locations}
            value={locationFilter}
            onChange={setLocationFilter}
            allowAll={true}
            allLabel={t("payroll_all_locations") || "Alle locaties"}
            testid="payroll-location-filter"
            ariaLabel={t("payroll_location_filter") || "Filter op locatie"}
            className="!h-10"
          />

          {/* Actions pushed right on wide screens */}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setShowSettings(true)}
              className="btn btn-ghost !h-10"
              data-testid="payroll-open-settings"
              title={t("payroll_settings_title")}
            >
              <Settings2 size={14} strokeWidth={2} />
              <span className="hidden sm:inline">{t("payroll_settings_title")}</span>
            </button>
            <button
              onClick={handleExportCsv}
              className="btn btn-ghost !h-10"
              data-testid="payroll-export-csv"
              title={t("payroll_export_csv") || "Exporteer CSV"}
              disabled={!overview || overview.rows.length === 0}
            >
              <Download size={14} strokeWidth={2} />
              <span className="hidden sm:inline">{t("payroll_export_csv") || "Exporteer CSV"}</span>
            </button>
            <button
              onClick={handlePrint}
              className="btn btn-primary !h-10"
              data-testid="payroll-print"
            >
              <Printer size={14} strokeWidth={2} />
              <span className="hidden sm:inline">{t("payroll_print")}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Print header (only visible in print) */}
      <div className="print-only mb-6">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[hsl(var(--text-muted))]">Rostr · {t("payroll_page_title")}</div>
        <div className="text-2xl font-display font-bold mt-1">{periodLabel}</div>
      </div>

      {/* KPI row */}
      <div ref={printRef} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="card p-5 flex items-center gap-4"
          data-testid="payroll-kpi-total"
        >
          <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center">
            <Wallet size={18} strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="eyebrow !text-[10px]">{t("payroll_grand_total")}</div>
            <div className="font-display text-[26px] font-bold tabular-nums leading-tight">
              {overview
                ? currencyFmt(overview.grand_total_amount, currency, lang)
                : "—"}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="card p-5 flex items-center gap-4"
          data-testid="payroll-kpi-hours"
        >
          <div className="w-11 h-11 rounded-xl bg-[hsl(160_65%_92%)] text-[hsl(160_65%_35%)] grid place-items-center">
            <Calculator size={18} strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="eyebrow !text-[10px]">{t("payroll_total_hours")}</div>
            <div className="font-display text-[26px] font-bold tabular-nums leading-tight">
              {overview
                ? <><AnimatedNumber value={overview.grand_total_hours} decimals={1} /><span className="text-[hsl(var(--text-muted))] text-[16px] ml-1">{t("payroll_hours_short")}</span></>
                : "—"}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="card p-5 flex items-center gap-4"
          data-testid="payroll-kpi-employees"
        >
          <div className="w-11 h-11 rounded-xl bg-[hsl(45_90%_92%)] text-[hsl(38_80%_45%)] grid place-items-center">
            <Coins size={18} strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="eyebrow !text-[10px]">{t("employees")}</div>
            <div className="font-display text-[26px] font-bold tabular-nums leading-tight">
              {overview ? overview.rows.length : "—"}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Rows */}
      {!overview ? (
        <div className="card p-6 text-sm text-[hsl(var(--text-muted))]">…</div>
      ) : overview.rows.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title={t("payroll_no_data_title")}
          hint={t("payroll_no_data_hint")}
        />
      ) : (
        <div className="card overflow-hidden" data-testid="payroll-rows">
          {/* Desktop column header — Feb 2026: amount moved to the very last
              column and widened, so the bedrag altijd rechts staat en zichtbaar
              blijft, ook bij smallere schermen. Actions krijgen 160px zodat de
              knoppen met tekst niet meer buiten hun cel duwen. */}
          <div className="hidden md:grid grid-cols-[1fr_120px_80px_120px_160px_180px] px-5 py-3 border-b hairline bg-[hsl(var(--muted))] eyebrow !text-[10px]">
            <div>{t("employee")}</div>
            <div className="text-right">{t("payroll_hourly_rate")}</div>
            <div className="text-right">{t("payroll_col_shifts") || "Diensten"}</div>
            <div className="text-right">{t("payroll_total_hours")}</div>
            <div className="text-right">{t("payroll_actions_col") || "Acties"}</div>
            <div className="text-right">{t("payroll_amount")}</div>
          </div>
          <div className="divide-y hairline">
            {overview.rows.map((row) => {
              const isOpen = !!expanded[row.user_id];
              const rateInfo = rateByUser[row.user_id];
              return (
                <div key={row.user_id} data-testid={`payroll-row-${row.user_id}`}>
                  {/* MOBILE row — stacked card layout */}
                  <div className="md:hidden p-4 hover:bg-[hsl(var(--muted))] transition-colors">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-[14px] text-[hsl(var(--text-strong))] truncate">{row.name}</div>
                        <div className="text-[11px] text-[hsl(var(--text-muted))] flex items-center gap-1.5 flex-wrap mt-0.5">
                          <span>{t(`role_${row.role}`) || row.role}</span>
                          {row.has_override && (
                            <Chip variant="primary" className="!text-[9px] !h-4 !px-1.5">override</Chip>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div
                          className={`font-display font-black text-[22px] tabular-nums leading-none ${row.total_amount > 0 ? "text-[hsl(var(--text-strong))]" : "text-[hsl(var(--text-muted))]"}`}
                          data-testid={`payroll-amount-mobile-${row.user_id}`}
                        >
                          {currencyFmt(row.total_amount, currency, lang)}
                        </div>
                        <div className="text-[11px] text-[hsl(var(--text-muted))] tabular-nums font-mono mt-1">{row.total_hours.toFixed(2)}{t("payroll_hours_short")} · {row.shifts.length}×</div>
                        {row.hourly_rate <= 0 && (
                          <div className="text-[10px] text-[hsl(38_80%_45%)] italic mt-0.5">{t("payroll_no_rate_short")}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => setEditingUser(rateInfo || { _id: row.user_id, name: row.name, role: row.role, hourly_rate: row.hourly_rate })}
                        className="btn btn-ghost !h-8 !text-[11.5px] !px-3 flex-1"
                        data-testid={`payroll-edit-mobile-${row.user_id}`}
                      >
                        <Pencil size={11} strokeWidth={2} />{row.hourly_rate > 0 ? currencyFmt(row.hourly_rate, currency, lang) + "/u" : t("payroll_no_rate_short")}
                      </button>
                      <button
                        onClick={() => setExpanded((e) => ({ ...e, [row.user_id]: !e[row.user_id] }))}
                        className="btn btn-ghost !h-8 !text-[11.5px] !px-3 flex-1"
                        data-testid={`payroll-toggle-mobile-${row.user_id}`}
                      >
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRightIcon size={12} />}
                        {isOpen ? t("payroll_hide_details") : t("payroll_view_details")}
                      </button>
                    </div>
                  </div>

                  {/* DESKTOP row */}
                  <div className="hidden md:grid grid-cols-[1fr_120px_80px_120px_160px_180px] gap-2 items-center px-5 py-4 hover:bg-[hsl(var(--muted))] transition-colors">
                    <div className="min-w-0">
                      <div className="font-semibold text-[14px] text-[hsl(var(--text-strong))] truncate">{row.name}</div>
                      <div className="text-[11px] text-[hsl(var(--text-muted))] flex items-center gap-1.5">
                        <span>{t(`role_${row.role}`) || row.role}</span>
                        {row.has_override && (
                          <Chip variant="primary" className="!text-[9px] !h-4 !px-1.5">override</Chip>
                        )}
                      </div>
                    </div>
                    <div className="text-right tabular-nums">
                      {row.hourly_rate > 0 ? (
                        <span className="font-mono text-[14px] font-semibold text-[hsl(var(--text-strong))]">{currencyFmt(row.hourly_rate, currency, lang)}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-1.5 h-5 rounded-full bg-[hsl(38_92%_50%_/_0.15)] text-[hsl(38_92%_35%)]">{t("payroll_no_rate_short")}</span>
                      )}
                    </div>
                    <div className="text-right text-[13px] text-[hsl(var(--text-muted))] tabular-nums">
                      {row.shifts.length}
                    </div>
                    <div className="text-right tabular-nums font-mono text-[14px] font-semibold text-[hsl(var(--text-strong))]">
                      {row.total_hours.toFixed(2)}<span className="text-[hsl(var(--text-muted))] ml-0.5 font-normal">{t("payroll_hours_short")}</span>
                    </div>
                    <div className="flex items-center justify-end gap-1 no-print">
                      <button
                        onClick={() => setEditingUser(rateInfo || { _id: row.user_id, name: row.name, role: row.role, hourly_rate: row.hourly_rate })}
                        className="btn btn-ghost !h-8 !text-[11.5px] !px-2.5"
                        title={t("payroll_edit_rate")}
                        data-testid={`payroll-edit-${row.user_id}`}
                      >
                        <Pencil size={12} strokeWidth={2} />
                        <span>{t("payroll_edit_short") || "Tarief"}</span>
                      </button>
                      <button
                        onClick={() => setExpanded((e) => ({ ...e, [row.user_id]: !e[row.user_id] }))}
                        className="btn btn-ghost !h-8 !text-[11.5px] !px-2.5"
                        aria-label={isOpen ? t("payroll_hide_details") : t("payroll_view_details")}
                        data-testid={`payroll-toggle-${row.user_id}`}
                      >
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRightIcon size={12} />}
                        <span>{isOpen ? t("payroll_hide_details") : t("payroll_view_details")}</span>
                      </button>
                    </div>
                    <div
                      className={`text-right tabular-nums font-display font-black text-[22px] leading-tight ${row.total_amount > 0 ? "text-[hsl(var(--text-strong))]" : "text-[hsl(var(--text-muted))]"}`}
                      data-testid={`payroll-amount-${row.user_id}`}
                    >
                      {currencyFmt(row.total_amount, currency, lang)}
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
                        className="overflow-hidden bg-[hsl(var(--bg))]"
                      >
                        <ShiftBreakdown shifts={row.shifts} currency={currency} lang={lang} t={t} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Print footer */}
      <div className="print-only mt-8 text-[11px] text-[hsl(var(--text-muted))]">
        {(t("payroll_print_footer") || "Gegenereerd op {when} · Rostr Loonoverzicht")
          .replace("{when}", new Date().toLocaleString(lang === "en" ? "en-GB" : "nl-NL"))}
      </div>

      {/* Settings drawer */}
      <AnimatePresence>
        {showSettings && settings && (
          <SettingsModal
            settings={settings}
            onClose={() => setShowSettings(false)}
            onSaved={(next) => { setSettings(next); load(); }}
            t={t}
          />
        )}
      </AnimatePresence>

      {/* Rate modal */}
      <AnimatePresence>
        {editingUser && (
          <RateModal
            user={editingUser}
            fullUser={rateByUser[editingUser._id] || editingUser}
            currency={currency}
            companyMultipliers={settings?.day_multipliers || {}}
            onClose={() => setEditingUser(null)}
            onSaved={() => { setEditingUser(null); load(); }}
            t={t}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


// ============================================================
// Shift breakdown (expandable per row)
// ============================================================
function ShiftBreakdown({ shifts, currency, lang, t }) {
  if (shifts.length === 0) {
    return <div className="px-5 py-4 text-[13px] text-[hsl(var(--text-muted))]">{t("payroll_no_shifts")}</div>;
  }
  return (
    <div className="px-5 py-3">
      <div className="hidden md:grid grid-cols-[100px_60px_120px_90px_90px_90px_100px_1fr] gap-3 pb-2 border-b hairline eyebrow !text-[10px]">
        <div>{t("date")}</div>
        <div>{t("day")}</div>
        <div>{t("time")?.length ? t("time") : "Tijd"}</div>
        <div className="text-right">{t("payroll_gross_hours")}</div>
        <div className="text-right">{t("payroll_pause")}</div>
        <div className="text-right">{t("payroll_net")}</div>
        <div className="text-right">{t("payroll_multiplier")}</div>
        <div className="text-right">{t("payroll_amount")}</div>
      </div>
      <div className="divide-y hairline">
        {shifts.map((s) => (
          <div key={s.shift_id} className="grid grid-cols-[100px_60px_120px_90px_90px_90px_100px_1fr] gap-3 py-2 text-[12px] items-center">
            <div className="tabular-nums font-mono">{s.date}</div>
            <div className="text-[hsl(var(--text-muted))] uppercase text-[10px] font-semibold tracking-wider">{t(`dow_${s.dow}`)}</div>
            <div className="tabular-nums font-mono text-[hsl(var(--text-muted))]">{s.start_time}–{s.end_time}</div>
            <div className="text-right tabular-nums">{s.gross_hours.toFixed(2)}</div>
            <div className="text-right tabular-nums text-[hsl(var(--text-muted))]">{s.pause_minutes > 0 ? `-${(s.pause_minutes / 60).toFixed(2)}` : "—"}</div>
            <div className="text-right tabular-nums font-semibold">{s.net_hours.toFixed(2)}</div>
            <div className="text-right tabular-nums">
              <span className={`inline-flex items-center px-2 h-5 rounded-full text-[10px] font-bold ${s.multiplier_pct === 100 ? "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]" : "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]"}`}>
                {s.multiplier_pct.toFixed(0)}%
              </span>
            </div>
            <div className="text-right tabular-nums font-mono font-semibold">{currencyFmt(s.amount, currency, lang)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================
// Settings modal (company multipliers)
// ============================================================
function SettingsModal({ settings, onClose, onSaved, t }) {
  const [mult, setMult] = useState({ ...settings.day_multipliers });
  const [busy, setBusy] = useState(false);

  const setDay = (k, v) => setMult((m) => ({ ...m, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const clean = {};
      for (const k of DOW) {
        const n = parseFloat(mult[k]);
        clean[k] = Number.isFinite(n) ? n : 100;
      }
      const { data } = await api.patch("/payroll/settings", { day_multipliers: clean });
      onSaved(data);
      toast.success(t("payroll_settings_saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="payroll-settings-modal"
    >
      <motion.div
        initial={{ scale: 0.96, y: 8, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 8, opacity: 0 }}
        transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
        className="card w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="eyebrow !text-[10px] mb-1">{t("settings")}</div>
            <div className="font-display text-[20px] font-bold">{t("payroll_settings_title")}</div>
            <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1">{t("payroll_settings_desc")}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))]" aria-label={t("cancel")}>
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {DOW.map((k) => (
            <label key={k} className="flex items-center gap-2">
              <span className="w-9 text-[11px] font-bold uppercase text-[hsl(var(--text-muted))]">{t(`dow_${k}`)}</span>
              <div className="relative flex-1">
                <input
                  type="number"
                  min="0" max="1000" step="5"
                  value={mult[k] ?? 100}
                  onChange={(e) => setDay(k, e.target.value)}
                  className="input-field !pr-8 tabular-nums font-mono"
                  data-testid={`payroll-mult-${k}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[hsl(var(--text-muted))] font-semibold">%</span>
              </div>
            </label>
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-ghost">{t("cancel")}</button>
          <button onClick={save} disabled={busy} className="btn btn-primary" data-testid="payroll-settings-save">
            {busy ? "…" : t("payroll_settings_save")}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


// ============================================================
// Rate modal (per-user)
// ============================================================
function RateModal({ user, fullUser, currency, companyMultipliers, onClose, onSaved, t }) {
  const initialRate = fullUser?.hourly_rate ?? user?.hourly_rate ?? "";
  const [rate, setRate] = useState(initialRate === null || initialRate === undefined ? "" : String(initialRate));
  const initialOverrides = fullUser?.day_multipliers || null;
  const [useOverride, setUseOverride] = useState(!!initialOverrides);
  const [overrides, setOverrides] = useState(() => {
    const src = initialOverrides || {};
    const out = {};
    for (const k of DOW) out[k] = src[k] !== undefined && src[k] !== null ? String(src[k]) : "";
    return out;
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      const parsed = parseFloat((rate + "").replace(",", "."));
      if (Number.isFinite(parsed)) body.hourly_rate = parsed;
      if (useOverride) {
        const dm = {};
        for (const k of DOW) {
          if (overrides[k] === "" || overrides[k] === null) continue;
          const n = parseFloat((overrides[k] + "").replace(",", "."));
          if (Number.isFinite(n)) dm[k] = n;
        }
        body.day_multipliers = dm;
      } else {
        body.clear_overrides = true;
      }
      await api.patch(`/payroll/rates/${user._id}`, body);
      toast.success(t("payroll_rate_saved"));
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="payroll-rate-modal"
    >
      <motion.div
        initial={{ scale: 0.96, y: 8, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 8, opacity: 0 }}
        transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
        className="card w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="eyebrow !text-[10px] mb-1">{t(`role_${user.role}`) || user.role}</div>
            <div className="font-display text-[20px] font-bold">{t("payroll_rate_modal_title", { name: user.name })}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))]" aria-label={t("cancel")}>
            <X size={16} />
          </button>
        </div>

        <label className="flex flex-col gap-1.5 mb-5">
          <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("payroll_rate_field_label")}</span>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder={t("payroll_rate_placeholder")}
              className="input-field !pl-8 tabular-nums font-mono"
              data-testid="payroll-rate-input"
              autoFocus
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[hsl(var(--text-muted))] font-semibold">€</span>
          </div>
        </label>

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useOverride}
            onChange={(e) => setUseOverride(e.target.checked)}
            className="w-4 h-4 accent-[hsl(var(--primary))]"
            data-testid="payroll-rate-toggle-override"
          />
          <span className="text-[13px] font-medium">{t("payroll_rate_override_toggle")}</span>
        </label>

        <AnimatePresence>
          {useOverride && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="text-[11px] text-[hsl(var(--text-muted))] mb-2">{t("payroll_rate_override_help")}</div>
              <div className="grid grid-cols-2 gap-2">
                {DOW.map((k) => (
                  <label key={k} className="flex items-center gap-2">
                    <span className="w-9 text-[11px] font-bold uppercase text-[hsl(var(--text-muted))]">{t(`dow_${k}`)}</span>
                    <div className="relative flex-1">
                      <input
                        type="number"
                        min="0" max="1000" step="5"
                        value={overrides[k]}
                        onChange={(e) => setOverrides((o) => ({ ...o, [k]: e.target.value }))}
                        placeholder={String(companyMultipliers[k] ?? 100)}
                        className="input-field !pr-8 !h-9 text-[13px] tabular-nums font-mono"
                        data-testid={`payroll-override-${k}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[hsl(var(--text-muted))] font-semibold">%</span>
                    </div>
                  </label>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-6 flex items-center justify-between gap-2">
          {fullUser?.day_multipliers && (
            <button
              onClick={() => { setUseOverride(false); setOverrides(() => { const o = {}; for (const k of DOW) o[k] = ""; return o; }); }}
              className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))] underline"
              data-testid="payroll-clear-overrides"
            >
              {t("payroll_rate_clear_override")}
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} className="btn btn-ghost">{t("cancel")}</button>
            <button onClick={save} disabled={busy} className="btn btn-primary" data-testid="payroll-rate-save">
              {busy ? "…" : t("save")}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
